export const LEAD_ENDPOINT = '/ansar/tg.php'
export const LEAD_REQUEST_TIMEOUT_MS = 20_000

export type LeadPayload = Record<string, unknown>
export type LeadFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>

export class LeadSubmissionError extends Error {
    constructor(public readonly kind: 'http' | 'response' | 'network' | 'timeout') {
        super(
            kind === 'http' ? 'Сервер не принял заявку.'
                : kind === 'response' ? 'Сервер вернул неожиданный ответ.'
                    : kind === 'timeout' ? 'Время ожидания ответа истекло. Повторите отправку.'
                        : 'Не удалось отправить заявку.',
        )
        this.name = 'LeadSubmissionError'
    }
}

const pendingSubmissions = new Map<string, Promise<void>>()

const requestLead = async (body: string, request: LeadFetch): Promise<void> => {
    const controller = new AbortController()
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    const timeout = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
            controller.abort()
            reject(new LeadSubmissionError('timeout'))
        }, LEAD_REQUEST_TIMEOUT_MS)
    })
    const beforeTimeout = <T>(promise: Promise<T>) => Promise.race([promise, timeout])
    let responseReceived = false

    try {
        const response = await beforeTimeout(request(LEAD_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json;charset=utf-8' },
            body,
            signal: controller.signal,
        }))
        responseReceived = true
        if (!response.ok) throw new LeadSubmissionError('http')

        const responseBody = await beforeTimeout(response.text())
        if (responseBody.trim() !== 'ok') throw new LeadSubmissionError('response')
    } catch (error) {
        if (error instanceof LeadSubmissionError) throw error
        throw new LeadSubmissionError(responseReceived ? 'response' : 'network')
    } finally {
        if (timeoutId !== undefined) clearTimeout(timeoutId)
    }
}

/**
 * Keeps one in-flight request per exact wire payload across component unmounts.
 * It does not provide server-side idempotency after a request has settled.
 */
export const submitLead = (payload: LeadPayload, request: LeadFetch = fetch): Promise<void> => {
    const body = JSON.stringify(payload)
    const key = `${LEAD_ENDPOINT}:${body}`
    const existing = pendingSubmissions.get(key)
    if (existing) return existing

    const submission = requestLead(body, request)
    pendingSubmissions.set(key, submission)
    void submission.finally(() => {
        if (pendingSubmissions.get(key) === submission) pendingSubmissions.delete(key)
    }).catch(() => undefined)
    return submission
}
